/**
 * Who uses the admin panel (add-support-system design §6). The server is the real gate: these lists only keep the
 * panel from offering a page that would answer 403.
 *
 * - The three spellings of "administrator" are FOLLOW-UPS #46; the panel admits all three, as it always has.
 * - `Support` works the support desk and nothing else: no money, salons, users, bookings, reviews, promotions or logs.
 * - The support desk itself admits `Admin`, `Administrator` and `Support` (`SupportDeskAttribute`), not `SuperAdmin`.
 */
export const SUPPORT_ROLE = 'Support'
export const ADMIN_ROLES: readonly string[] = ['Admin', 'SuperAdmin', 'Administrator']
export const PANEL_ROLES: readonly string[] = [...ADMIN_ROLES, SUPPORT_ROLE]
export const SUPPORT_DESK_ROLES: readonly string[] = ['Admin', 'Administrator', SUPPORT_ROLE]
/** Who may grant and revoke the Support role (`AdminUserRolesController`: Admin, Administrator). */
export const SUPPORT_GRANTER_ROLES: readonly string[] = ['Admin', 'Administrator']
/** The support report card: everyone at the support desk. */
export const SUPPORT_REPORT_ROLES = SUPPORT_DESK_ROLES
/** The dashboard: administrators see all of it, an agent only the support report card. */
export const DASHBOARD_ROLES: readonly string[] = PANEL_ROLES

/** Role names compared without regard to case: the token's spelling is the server's business. */
export function hasAnyRole(roles: readonly string[] | null | undefined, allowed: readonly string[]): boolean {
  if (!roles?.length) return false
  const wanted = new Set(allowed.map((r) => r.toLowerCase()))
  return roles.some((r) => typeof r === 'string' && wanted.has(r.toLowerCase()))
}

export const isAdminRole = (roles: readonly string[] | null | undefined) => hasAnyRole(roles, ADMIN_ROLES)
export const canEnterPanel = (roles: readonly string[] | null | undefined) => hasAnyRole(roles, PANEL_ROLES)
export const canUseSupportDesk = (roles: readonly string[] | null | undefined) => hasAnyRole(roles, SUPPORT_DESK_ROLES)

/** A support agent who is not also an administrator: sees the Support section only. */
export const isSupportOnly = (roles: readonly string[] | null | undefined) =>
  hasAnyRole(roles, [SUPPORT_ROLE]) && !isAdminRole(roles)

/** Where someone lands after signing in, and where a page they may not open sends them. */
export const homePathFor = (roles: readonly string[] | null | undefined) =>
  isSupportOnly(roles) ? '/support' : '/dashboard'
